import { useMemo, useState } from "react";
import PageBanner from "@/components/feature/PageBanner";
import { SkeletonCard } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import { useApi } from "@/hooks/useApi";
import { useNow } from "@/hooks/useNow";
import { useAuth } from "@/components/feature/AuthProvider";
import type { TreatBrief } from "@/lib/types";
import TreatCard from "./components/TreatCard";
import TreatWriteModal from "./components/TreatWriteModal";

// /treat — 오늘의 번개 + 지난 모임
export default function TreatPage() {
  const { data, loading, error, reload, serverTs } = useApi<TreatBrief[]>("treats.php");
  const now = useNow();
  const { member, requireLogin } = useAuth();
  const [writeOpen, setWriteOpen] = useState(false);
  const [pastOpen, setPastOpen] = useState(false);

  const elapsed = serverTs ? Math.max(0, now / 1000 - serverTs) : 0;

  const { today, past } = useMemo(() => {
    const list = data ?? [];
    return {
      today: list.filter((t) => Number(t.is_today) === 1),
      past: list.filter((t) => Number(t.is_today) !== 1),
    };
  }, [data]);

  const openWrite = () => {
    if (!member) {
      requireLogin("/treat");
      return;
    }
    setWriteOpen(true);
  };

  const handleCreated = (no?: number) => {
    setWriteOpen(false);
    reload();
    if (no) {
      window.setTimeout(() => {
        document
          .getElementById(`treat-${no}`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 350);
    }
  };

  return (
    <>
      <PageBanner
        title="오늘은 내가 쏜다"
        description="예: 오늘 점심 쏠 테니 선착순으로 오세요. 회원 누구나 가볍게 올릴 수 있는 번개입니다."
        breadcrumb={[{ label: "내가쏜다" }]}
      />

      <section className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-12">
        {/* 오늘 섹션 헤더 */}
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            오늘
            {!loading && today.length > 0 && (
              <span className="rounded-full bg-primary-100 px-2.5 py-0.5 text-sm font-semibold text-primary-700">
                {today.length}
              </span>
            )}
          </h2>
          <button
            type="button"
            onClick={openWrite}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
          >
            <i className="ri-add-line text-base" aria-hidden="true"></i>
            내가 쏜다
          </button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : error ? (
          <EmptyState
            icon="ri-error-warning-line"
            title="번개 목록을 불러오지 못했습니다"
            description={error}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                다시 시도
              </button>
            }
          />
        ) : today.length === 0 ? (
          <div className="rounded-xl border border-dashed border-background-300 bg-background-50 px-6 py-14 text-center">
            <span className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
              <i className="ri-hand-heart-line" aria-hidden="true"></i>
            </span>
            <p className="font-heading text-base font-semibold text-foreground-800">
              오늘은 아직 쏘는 사람이 없습니다.
            </p>
            <p className="mt-1.5 text-sm text-foreground-500">
              첫 번째가 되어보세요!
            </p>
            <button
              type="button"
              onClick={openWrite}
              className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
            >
              <i className="ri-add-line text-base" aria-hidden="true"></i>
              내가 쏜다
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {today.map((t) => (
              <TreatCard key={t.no} brief={t} elapsed={elapsed} onReload={reload} />
            ))}
          </div>
        )}

        {/* 지난 섹션 (접이식 · 기본 접힘) */}
        <div className="mt-12">
          <button
            type="button"
            onClick={() => setPastOpen((v) => !v)}
            aria-expanded={pastOpen}
            className="flex w-full items-center justify-between rounded-xl border border-background-200 bg-background-100 px-5 py-3.5 text-left hover:bg-background-200/60"
          >
            <span className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-800">
              <i className="ri-history-line text-foreground-500" aria-hidden="true"></i>
              지난 모임 보기
              <span className="font-normal text-foreground-500">({past.length}건)</span>
            </span>
            <i
              className={`text-lg text-foreground-500 transition-transform ${
                pastOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"
              }`}
              aria-hidden="true"
            ></i>
          </button>

          {pastOpen && (
            <div className="mt-5">
              {past.length === 0 ? (
                <p className="rounded-xl border border-background-200 bg-background-50 px-5 py-8 text-center text-sm text-foreground-500">
                  아직 지난 모임이 없습니다.
                </p>
              ) : (
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                  {past.map((t) => (
                    <TreatCard
                      key={t.no}
                      brief={t}
                      elapsed={elapsed}
                      onReload={reload}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <TreatWriteModal
        open={writeOpen}
        onClose={() => setWriteOpen(false)}
        onCreated={handleCreated}
      />
    </>
  );
}